import { useCallback, useEffect, useRef, useState } from 'react';
import { data, Form, useFetcher, useSearchParams } from 'react-router';
import { Result } from 'ts-results-es';
import { zx } from 'zodix';

import { EmptySetMessage } from '~/components/content/empty-set-message';
import { useInfiniteScroll } from '~/components/infinite-scroll';
import { LinkWithSrc } from '~/components/link-with-src';
import { SearchInput, searchInputSchema } from '~/components/search-input';
import { searchEvents } from '~/domain/events/handlers/search-events.server';
import { type EventResult } from '~/domain/events/models/event-result';
import { getEventCities } from '~/domain/shared/queries/get-event-cities.server';
import { getEventZipCodes } from '~/domain/shared/queries/get-event-zip-codes.server';
import { paginationSchema } from '~/domain/shared/schemas/filter-options';
import { handleDomainError } from '~/utils/errors.server';
import { parseCityStateZip } from '~/utils/parse-city-state-zip.server';

import { type Route } from './+types/_index';
import { EventCard } from './components/event-card';

export const handle = {
  title: 'Find Events',
};

const PAGE_SIZE = 50;
const CACHE_HEADERS = { headers: { 'Cache-Control': 'public, max-age=120' } };

export async function loader({ request }: Route.LoaderArgs) {
  const { searchParams } = new URL(request.url);
  const paginationOptionsResult = zx.parseQuerySafe(searchParams, paginationSchema);
  const { pageIndex, pageSize } = paginationOptionsResult.success
    ? paginationOptionsResult.data
    : { pageIndex: 0, pageSize: PAGE_SIZE };

  const { search, city: rawCity, lat, long, dist } = zx.parseQuery(request, searchInputSchema);

  const { city, state, zip } = parseCityStateZip(rawCity);

  const eventSearchParams = {
    search,
    city,
    stateAbbreviation: state,
    zip,
    pageIndex,
    pageSize,
    lat,
    long,
    dist,
  };

  // Optimization -- When infinite scrolling, only load event data
  if (searchParams.has('scroll')) {
    const eventsResult = await searchEvents(eventSearchParams);
    const events = eventsResult.mapErr(handleDomainError).unwrap();
    return data({ events, cities: null, zipCodes: null }, CACHE_HEADERS);
  }

  // Otherwise get everything for the initial page load
  const [eventsResult, citiesResult, zipCodesResult] = await Promise.all([
    searchEvents(eventSearchParams),
    getEventCities(),
    getEventZipCodes(),
  ]);
  const result = Result.all(eventsResult, citiesResult, zipCodesResult);

  return result
    .map(([events, cities, zipCodes]) =>
      data(
        {
          events,
          cities,
          zipCodes,
        },
        CACHE_HEADERS,
      ),
    )
    .mapErr(handleDomainError)
    .unwrap();
}

const buildQuery = (searchParams: URLSearchParams, pageIndex: number) => {
  const query = new URLSearchParams(searchParams);
  query.set('pageIndex', String(pageIndex));
  query.set('pageSize', String(PAGE_SIZE));
  query.set('scroll', '1');
  return `?index&${query}`;
};

function SearchResults({ title, results }: { title?: string; results: EventResult[] }) {
  return (
    <>
      {title ? <div className="px-8 text-subtitle2 tablet:px-12 desktop:px-0">{title}</div> : null}
      <div className="grid w-full gap-4 tablet:grid-cols-[repeat(auto-fill,minmax(350px,1fr))] tablet:px-12 desktop:px-0">
        {results.map((event) => (
          <LinkWithSrc to={`/events/${event.id}`} key={event.id} className="flex flex-col">
            <EventCard event={event} className="flex-1 desktop:w-[408px]" />
          </LinkWithSrc>
        ))}
      </div>
    </>
  );
}

export default function Search({
  loaderData: {
    events: { rows: initialRows, rowCount: initialRowCount },
    cities,
    zipCodes,
  },
}: Route.ComponentProps) {
  const [searchParams] = useSearchParams();
  const { data, state, load } = useFetcher<typeof loader>();
  const pageIndex = useRef(0);
  const [rows, setRows] = useState(initialRows);
  const [rowCount, setRowCount] = useState(initialRowCount);

  const loadPage = useCallback(() => {
    pageIndex.current++;
    if (pageIndex.current * PAGE_SIZE < rowCount) {
      load(buildQuery(searchParams, pageIndex.current));
    }
  }, [rowCount, searchParams, load]);

  // User scrolled to a new page and new rows were loaded, so append those to
  // the infinite-scroll state
  useEffect(() => {
    if (data && state === 'idle') {
      const { rows, rowCount } = data.events;
      setRows((prev) => [...prev, ...rows]);
      setRowCount(rowCount);
    }
  }, [data, state]);

  // Search params were updated, so reinitialize the infinite-scroll state
  useEffect(() => {
    setRows(initialRows);
    setRowCount(initialRowCount);
    pageIndex.current = 0;
  }, [load, initialRows, initialRowCount]);

  const comboboxItems = [
    ...cities!.map(({ city, state }) => `${city}, ${state}`),
    ...zipCodes!.map((z) => z?.toString() ?? ''),
  ];

  const loading = state === 'loading';
  const { loadingSentinel, spinner } = useInfiniteScroll({ loading, loadPage });

  const renderEvents = () => {
    if (rows.length === 0) {
      const hasSearch = Array.from(searchParams.values()).some((value) => value);
      if (hasSearch) {
        return (
          <EmptySetMessage
            title="Sorry, no results match your search."
            message="Check your spelling or try other search terms."
          />
        );
      }

      return (
        <EmptySetMessage
          title="There are currently no events planned. Check back soon!"
          message=""
        />
      );
    }

    return (
      <>
        <SearchResults title={rows.length > 0 ? 'Events' : undefined} results={rows} />
        {loadingSentinel}
        {spinner}
      </>
    );
  };

  return (
    <div className="flex size-full flex-col">
      <div className="-mt-8 rounded-b-2xl bg-gray-200 px-2 pt-4 tablet:px-12">
        <div className="mx-auto mt-4 max-w-screen-xl">
          <Form>
            <div className="py-2">
              <SearchInput
                namePlaceholder="Search"
                comboboxItems={comboboxItems}
                searchParams={searchParams}
                className="px-4 tablet:py-2"
              />
            </div>
            <button type="submit" className="hidden">
              {/* this is only here to make the form submit when the user presses enter */}
            </button>
          </Form>
        </div>
      </div>

      <div className="mx-auto flex size-full max-w-screen-xl flex-col gap-y-6 pt-6">
        {renderEvents()}
      </div>
    </div>
  );
}
